import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  Button,
  MenuItem,
  Paper,
  Box,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../../services/api';

function NotificacoesHistorico() {
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    tipo: '',
    canal: '',
    lida: ''
  });

  const buscarNotificacoes = async () => {
    try {
      const response = await api.get('/notificacoes');
      
      // Aplicar filtros localmente
      let notificacoesFiltradas = response.data;
      
      if (filtros.dataInicio && filtros.dataFim) {
        notificacoesFiltradas = notificacoesFiltradas.filter(n => {
          const data = new Date(n.data_criacao);
          return data >= new Date(filtros.dataInicio) && data <= new Date(filtros.dataFim);
        });
      }
      
      if (filtros.tipo) {
        notificacoesFiltradas = notificacoesFiltradas.filter(n => n.tipo === filtros.tipo);
      }
      
      if (filtros.canal) {
        notificacoesFiltradas = notificacoesFiltradas.filter(n => n.canal === filtros.canal);
      }
      
      if (filtros.lida !== '') {
        const lida = filtros.lida === 'true';
        notificacoesFiltradas = notificacoesFiltradas.filter(n => n.lida === lida);
      }
      
      setNotificacoes(notificacoesFiltradas);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      toast.error('Erro ao buscar notificações');
      setNotificacoes([]);
    }
  };

  // Buscar notificações quando o componente montar ou quando os filtros mudarem
  React.useEffect(() => {
    buscarNotificacoes();
  }, [filtros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const executarVerificacao = async () => {
    try {
      setLoading(true);
      const response = await api.post('/notificacoes/executar-verificacao');
      toast.success(response.data.mensagem);
      await buscarNotificacoes(); // Atualiza a lista após executar
    } catch (error) {
      console.error('Erro ao executar verificação:', error);
      toast.error('Erro ao executar verificação de vencimentos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, pl: { xs: 1, md: 1.5 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Histórico de Notificações
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={executarVerificacao}
          disabled={loading}
        >
          {loading ? 'Executando...' : 'Executar Verificação'}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  label="Data Início"
                  value={filtros.dataInicio}
                  onChange={(newValue) => handleFiltroChange('dataInicio', newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  label="Data Fim"
                  value={filtros.dataFim}
                  onChange={(newValue) => handleFiltroChange('dataFim', newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={filtros.tipo}
                onChange={(e) => handleFiltroChange('tipo', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="cobranca">Cobrança</MenuItem>
                <MenuItem value="parcela_atrasada">Parcela Atrasada</MenuItem>
                <MenuItem value="vencimento_proximo">Vencimento Próximo</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="Canal"
                value={filtros.canal}
                onChange={(e) => handleFiltroChange('canal', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="sistema">Sistema</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="Status"
                value={filtros.lida}
                onChange={(e) => handleFiltroChange('lida', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Lida</MenuItem>
                <MenuItem value="false">Não Lida</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Mensagem</TableCell>
                  <TableCell>Canal</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificacoes.map((notificacao) => (
                  <TableRow key={notificacao.id}>
                    <TableCell>{format(new Date(notificacao.data_criacao), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell>{notificacao.cliente?.nome || '-'}</TableCell>
                    <TableCell>{notificacao.tipo}</TableCell>
                    <TableCell>{notificacao.mensagem}</TableCell>
                    <TableCell>{notificacao.canal}</TableCell>
                    <TableCell>{notificacao.lida ? 'Lida' : 'Não Lida'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default NotificacoesHistorico;